import { useState, useEffect } from 'react';
import { Alarm } from '@/api/alarms/types';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { AlertTriangle, AlertCircle, Info, CheckCircle, Clock, Brain, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Markdown } from '@/components/ui/markdown';

interface AlarmDetailProps {
  alarm: Alarm | null;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
  isLoading: boolean;
}

export function AlarmDetail({ alarm, onAcknowledge, onResolve, isLoading }: AlarmDetailProps) {
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);

  // Generate AI explanation and suggestion when an alarm is selected
  useEffect(() => {
    if (alarm) {
      generateAiInsights(alarm);
    } else {
      setAiExplanation(null);
      setAiSuggestion(null);
    }
  }, [alarm]);

  const generateAiInsights = (alarm: Alarm) => {
    setIsAiLoading(true);

    // Simulate AI response delay
    setTimeout(() => {
      setAiExplanation(generateMockExplanation(alarm));
      setAiSuggestion(generateMockSuggestion(alarm));
      setIsAiLoading(false);
    }, 800);
  };
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex space-x-2 px-3 py-2">
          <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    );
  }

  if (!alarm) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="text-gray-400 mb-2">
          <AlertCircle className="h-12 w-12 mx-auto stroke-[1.5px]" />
        </div>
        <h3 className="text-lg font-medium text-dark-blue mb-1">No alarm selected</h3>
        <p className="text-sm text-dark-grey">
          Select an alarm from the list to view its details.
        </p>
      </div>
    );
  }

  const canAcknowledge = alarm.status === 'active';
  const canResolve = alarm.status === 'active' || alarm.status === 'acknowledged';

  return (
    <div style={{
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        padding: '12px',
        borderBottom: '1px solid var(--color-light-grey)',
        flexShrink: 0,
        backgroundColor: 'white',
        zIndex: 1
      }}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-medium text-black">{alarm.deviceName}</h2>
            <p className="text-xs text-dark-grey">{alarm.deviceId}</p>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canAcknowledge}
              onClick={() => onAcknowledge(alarm.id)}
              className={cn(
                "border-primary text-primary",
                !canAcknowledge && "opacity-50 cursor-not-allowed"
              )}
            >
              <Clock className="h-3.5 w-3.5 mr-1" />
              Acknowledge
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canResolve}
              onClick={() => onResolve(alarm.id)}
              className={cn(
                "border-success text-dark-green",
                !canResolve && "opacity-50 cursor-not-allowed"
              )}
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              Resolve
            </Button>
          </div>
        </div>
      </div>

      <div style={{
        position: 'absolute',
        top: '60px',
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        padding: '12px'
      }}>
        <div className="mb-4">
          <h3 className="text-xs font-medium text-black mb-2">Alarm Information</h3>
          <div className="bg-white rounded-lg border border-light-grey overflow-hidden shadow-sm">
            <div className="px-3 py-2 border-b border-light-grey bg-bg">
              <div className="flex items-center">
                <AlarmSeverityIcon severity={alarm.severity} />
                <span className="ml-2 text-sm font-medium text-black">{alarm.message}</span>
              </div>
            </div>
            <div className="px-3 py-2 grid grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-dark-grey">Alarm Code</p>
                <p className="text-sm font-medium text-black">{alarm.code}</p>
              </div>
              <div>
                <p className="text-xs text-dark-grey">Type</p>
                <p className="text-sm font-medium text-black capitalize">{alarm.type}</p>
              </div>
              <div>
                <p className="text-xs text-dark-grey">Severity</p>
                <p className="text-sm font-medium text-black capitalize">{alarm.severity}</p>
              </div>
              <div>
                <p className="text-xs text-dark-grey">Status</p>
                <p className="text-sm font-medium text-black capitalize">{alarm.status}</p>
              </div>
              <div>
                <p className="text-xs text-dark-grey">Device ID</p>
                <p className="text-sm font-medium text-black">{alarm.deviceId}</p>
              </div>
              <div>
                <p className="text-xs text-dark-grey">Timestamp</p>
                <p className="text-sm font-medium text-black">
                  {format(new Date(alarm.timestamp), 'PPpp')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <h3 className="text-xs font-medium text-black mb-1.5">Details</h3>
          <div className="bg-white rounded-lg border border-light-grey p-3 shadow-sm">
            <p className="text-xs text-gray-700">{alarm.details || 'No additional details available.'}</p>
          </div>
        </div>

        {/* AI Explanation Section */}
        <div className="mb-3">
          <div className="flex items-center mb-1.5">
            <h3 className="text-xs font-medium text-black">AI Explanation</h3>
            {isAiLoading && (
              <div className="flex gap-0.5 ml-2">
                <div className="w-1 h-1 bg-primary rounded-full animate-loading-dots"></div>
                <div className="w-1 h-1 bg-primary rounded-full animate-loading-dots delay-150"></div>
                <div className="w-1 h-1 bg-primary rounded-full animate-loading-dots delay-300"></div>
              </div>
            )}
          </div>
          <div className="bg-white rounded-lg border border-light-grey p-3 shadow-sm">
            {isAiLoading ? (
              <div className="flex items-center justify-center py-2">
                <div className="h-4 w-4 rounded-full bg-gray-200 animate-pulse"></div>
              </div>
            ) : (
              <div className="flex items-start">
                <Brain className="h-3.5 w-3.5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                <Markdown
                  content={aiExplanation || 'No explanation available.'}
                  className="text-xs text-black markdown-content"
                />
              </div>
            )}
          </div>
        </div>

        {/* AI Suggested Next Action Section */}
        <div className="mb-3">
          <h3 className="text-xs font-medium text-black mb-1.5">Suggested Next Action</h3>
          <div className="bg-white rounded-lg border border-light-grey p-3 shadow-sm">
            {isAiLoading ? (
              <div className="flex items-center justify-center py-2">
                <div className="h-4 w-4 rounded-full bg-gray-200 animate-pulse"></div>
              </div>
            ) : (
              <div className="flex items-start">
                <Brain className="h-3.5 w-3.5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                <Markdown
                  content={aiSuggestion || 'No suggestions available.'}
                  className="text-xs text-black markdown-content"
                />
              </div>
            )}
          </div>
        </div>

        {(alarm.status === 'acknowledged' || alarm.status === 'resolved') && (
          <div className="mb-3">
            <h3 className="text-xs font-medium text-black mb-1.5">Activity</h3>
            <div className="bg-white rounded-lg border border-light-grey overflow-hidden shadow-sm">
              {alarm.acknowledgedBy && (
                <div className="px-3 py-2 border-b border-light-grey">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <Clock className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="ml-2">
                      <p className="text-xs text-gray-700">Acknowledged by {alarm.acknowledgedBy}</p>
                      <p className="text-[10px] text-dark-grey">
                        {format(new Date(alarm.acknowledgedAt!), 'PPpp')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {alarm.resolvedBy && (
                <div className="px-3 py-2">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-3.5 w-3.5 text-success" />
                    </div>
                    <div className="ml-2">
                      <p className="text-xs text-gray-700">Resolved by {alarm.resolvedBy}</p>
                      <p className="text-[10px] text-dark-grey">
                        {format(new Date(alarm.resolvedAt!), 'PPpp')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AlarmSeverityIcon({ severity }: { severity: string }) {
  switch (severity) {
    case 'critical':
      return <AlertCircle className="h-4 w-4 text-danger" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-light-orange" />;
    case 'info':
      return <Info className="h-4 w-4 text-primary" />;
    default:
      return null;
  }
}

// Mock explanation generator
function generateMockExplanation(alarm: Alarm): string {
  const explanations = {
    temperature: `This alarm indicates that the temperature in ${alarm.deviceName} has exceeded normal operating parameters.

* **Issue**: Temperature outside acceptable range (${alarm.code})
* **Severity**: ${alarm.severity.charAt(0).toUpperCase() + alarm.severity.slice(1)}
* **Potential Impact**: Reduced equipment performance and longevity
* **Affected System**: Cooling/HVAC
* **Detection Method**: Temperature sensor readings`,

    communication: `This alarm indicates a communication failure between the control system and ${alarm.deviceName}.

* **Issue**: Unable to establish reliable connection (${alarm.code})
* **Severity**: ${alarm.severity.charAt(0).toUpperCase() + alarm.severity.slice(1)}
* **Potential Impact**: Loss of monitoring and control capabilities
* **Affected System**: Network/Communication infrastructure
* **Detection Method**: Connection timeout or error response`,

    power: `This alarm indicates a power-related issue with ${alarm.deviceName}.

* **Issue**: Abnormal power conditions detected (${alarm.code})
* **Severity**: ${alarm.severity.charAt(0).toUpperCase() + alarm.severity.slice(1)}
* **Potential Impact**: Unstable operation or potential damage
* **Affected System**: Power supply/Electrical
* **Detection Method**: Power monitoring sensors`,

    sensor: `This alarm indicates a sensor malfunction in ${alarm.deviceName}.

* **Issue**: Inconsistent or out-of-range readings (${alarm.code})
* **Severity**: ${alarm.severity.charAt(0).toUpperCase() + alarm.severity.slice(1)}
* **Potential Impact**: Inaccurate monitoring and control
* **Affected System**: Sensor network
* **Detection Method**: Data validation checks`,

    system: `This alarm indicates a system-level issue with ${alarm.deviceName}.

* **Issue**: Internal error reported (${alarm.code})
* **Severity**: ${alarm.severity.charAt(0).toUpperCase() + alarm.severity.slice(1)}
* **Potential Impact**: Reduced functionality or system failure
* **Affected System**: Core system software/hardware
* **Detection Method**: Internal diagnostics`
  };

  return explanations[alarm.type as keyof typeof explanations] ||
    `This is a ${alarm.severity} ${alarm.type} alarm for ${alarm.deviceName}.

* **Issue**: System detected an anomaly (${alarm.code})
* **Severity**: ${alarm.severity.charAt(0).toUpperCase() + alarm.severity.slice(1)}
* **Status**: ${alarm.status.charAt(0).toUpperCase() + alarm.status.slice(1)}
* **Detection Time**: ${format(new Date(alarm.timestamp), 'PPpp')}`;
}

// Mock suggestion generator
function generateMockSuggestion(alarm: Alarm): string {
  const statusHeaders = {
    active: `**This alarm requires immediate attention.**`,
    acknowledged: `**This alarm has been acknowledged but still needs to be resolved.**`,
    resolved: `**This alarm has been resolved, but you may want to verify that the issue doesn't recur.**`
  };

  const suggestions = {
    temperature: {
      critical: `
* Check the cooling system immediately
* Verify airflow is not restricted
* Ensure cooling components are functioning properly
* Monitor temperature readings continuously
* If temperature continues to rise, consider shutting down the system to prevent damage
* Document all actions taken for maintenance records`,
      warning: `
* Inspect the ventilation and cooling systems
* Check for dust buildup or blocked air intakes
* Clean filters if necessary
* Monitor the temperature trend over the next hour
* Schedule a thorough inspection if this is a recurring issue`,
      info: `
* Monitor the temperature readings periodically
* Schedule routine maintenance to check the cooling system
* Review historical temperature data for patterns
* Ensure environmental conditions are within specifications`
    },
    communication: {
      critical: `
* Check physical network connections and power to the device
* Restart communication interfaces if possible
* Verify network settings and configurations
* Test alternative communication paths if available
* Document connection issues for IT team review`,
      warning: `
* Test the connection to the device
* Check for interference or network congestion
* Consider restarting the device if issues persist
* Monitor connection stability over the next few hours`,
      info: `
* Monitor the communication status periodically
* Consider running diagnostics on the network
* Document any intermittent issues for future reference`
    },
    power: {
      critical: `
* Check the power supply and electrical connections immediately
* Measure voltage levels if possible
* Look for signs of overheating or damage
* Consider involving an electrician if the issue appears to be with the building's electrical system
* Prepare backup power options if available`,
      warning: `
* Monitor power readings closely
* Check for loose connections or signs of electrical issues
* Consider a power quality assessment
* Document any fluctuations for maintenance team`,
      info: `
* Keep an eye on power readings periodically
* Schedule an inspection of electrical components during the next maintenance cycle
* Review power consumption patterns for anomalies`
    },
    sensor: {
      critical: `
* Verify sensor readings with alternative measurement methods if possible
* Check sensor connections and wiring
* Look for physical damage to the sensor
* Consider replacing the sensor if readings remain inconsistent
* Document calibration requirements for maintenance`,
      warning: `
* Compare sensor readings with expected values
* Check sensor calibration and connections
* Clean sensors if applicable
* Test sensor response to controlled changes`,
      info: `
* Monitor sensor readings for further anomalies
* Schedule sensor calibration during the next maintenance cycle
* Document sensor performance for trending analysis`
    },
    system: {
      critical: `
* Restart the system if safe to do so
* Check system logs for specific error codes
* Save diagnostic information before taking action
* Contact technical support if the issue persists after restart
* Prepare alternative operational procedures if system remains down`,
      warning: `
* Check system logs for error details
* Verify system configurations
* Consider a controlled restart during a low-impact period
* Monitor system performance closely after any changes`,
      info: `
* Monitor system performance periodically
* Check for available software or firmware updates
* Schedule preventive maintenance during next service window`
    }
  };

  const statusHeader = statusHeaders[alarm.status as keyof typeof statusHeaders] || '';
  const typeSuggestion = suggestions[alarm.type as keyof typeof suggestions]?.[alarm.severity as keyof (typeof suggestions)['temperature']] || `
* Investigate the ${alarm.type} issue thoroughly
* Document your findings
* Determine the appropriate response based on your investigation
* Consider consulting with technical support if needed`;

  return `${statusHeader}\n\n**Recommended Actions:**${typeSuggestion}`;
}
